import * as path from "path";
import * as fs from "fs";
import * as glob from "glob";
import { LSPClient, SymbolKind } from "./utils/lsp_core";
import { checkRealFuncChar } from "./utils/symbol_utils";

export interface ShowOptions {
  file: string;
  symbol?: string;
  startLine?: number;
  endLine?: number;
  mode?: "full" | "methods" | "init";
  contextLines?: number;
}

export interface ShowResult {
  file: string;
  symbol?: string;
  mode?: string;
  startLine: number;
  endLine: number;
  content: string;
  totalLines: number;
}

// 读取文件内容
function readFileLines(filePath: string): string[] {
  const absPath = path.resolve(filePath);
  if (!fs.existsSync(absPath)) throw new Error(`文件不存在: ${filePath}`);
  return fs.readFileSync(absPath, "utf-8").split("\n");
}

// 展示文件区间
export function showFileRange(options: ShowOptions): ShowResult {
  const { file, startLine = 1, endLine = -1, contextLines = 0 } = options;
  const lines = readFileLines(file);
  const totalLines = lines.length;
  const actualStart = Math.max(1, startLine - contextLines);
  const actualEnd = endLine === -1 ? totalLines : Math.min(totalLines, endLine + contextLines);
  const content = lines.slice(actualStart - 1, actualEnd).join("\n");
  return {
    file,
    startLine: actualStart,
    endLine: actualEnd,
    content,
    totalLines,
  };
}

function findClassSymbol(symbols: any[], className: string): any | undefined {
  for (const s of symbols) {
    if (s.name === className && s.kind === SymbolKind.Class) return s;
    if (s.children && s.children.length > 0) {
      const found = findClassSymbol(s.children, className);
      if (found) return found;
    }
  }
  return undefined;
}

function formatMethodSignature(method: any): string {
  // 优先输出 detail 字段（如 foo(a: number): string），否则输出 name
  if (method.detail && typeof method.detail === 'string') {
    return `${method.name}${method.detail.startsWith('(') ? '' : ' '}${method.detail}`;
  }
  return method.name;
}

function collectMethods(symbol: any): string[] {
  let methods: string[] = [];
  if (symbol.kind === SymbolKind.Method || symbol.kind === SymbolKind.Constructor) {
    methods.push(formatMethodSignature(symbol));
  }
  if (symbol.children && symbol.children.length > 0) {
    for (const child of symbol.children) {
      methods = methods.concat(collectMethods(child));
    }
  }
  return methods;
}

function collectInit(symbol: any): string[] {
  let items: string[] = [];
  if (symbol.kind === SymbolKind.Property) {
    items.push(`${symbol.name}${symbol.detail ? ': ' + symbol.detail : ''}`);
  }
  if (symbol.kind === SymbolKind.Constructor) {
    items.push(formatMethodSignature(symbol));
  }
  if (symbol.children && symbol.children.length > 0) {
    for (const child of symbol.children) {
      items = items.concat(collectInit(child));
    }
  }
  return items;
}

function collectMethodsFlat(symbols: any[], className: string): string[] {
  return symbols
    .filter((s: any) =>
      s.containerName === className &&
      (s.kind === SymbolKind.Method || s.kind === SymbolKind.Constructor)
    )
    .map(formatMethodSignature);
}

function collectInitFlat(symbols: any[], className: string): string[] {
  return symbols
    .filter((s: any) =>
      s.containerName === className &&
      (s.kind === SymbolKind.Property || s.kind === SymbolKind.Constructor)
    )
    .map((s: any) =>
      s.kind === SymbolKind.Constructor ? formatMethodSignature(s) : `${s.name}${s.detail ? ': ' + s.detail : ''}`
    );
}

async function getMethodSignature(lsp: any, file: string, symbol: any): Promise<string> {
  const absPath = path.resolve(file);
  const uri = `file://${absPath}`;
  const content = fs.readFileSync(absPath, "utf-8");
  let pos = {
    line: symbol.location.range.start.line,
    character: symbol.location.range.start.character
  };
  // 用 checkRealFuncChar 精确定位方法名
  try {
    const lines = content.split("\n");
    // 获取 symbol 的完整代码块（多行，适配 Python 装饰器）
    let blockLines = lines.slice(
      symbol.location.range.start.line,
      symbol.location.range.end.line + 1
    ).join("\n");
    const lineText = blockLines;
    const realPos = checkRealFuncChar(
      lineText,
      pos.line,
      pos.character,
      symbol.name,
      symbol.kind,
      uri
    );
    if (realPos && realPos.line !== undefined && realPos.character !== undefined) {
      pos = { line: realPos.line, character: realPos.character };
    }
  } catch (e) {
    console.error('[checkRealFuncChar error]', e);
  }
  let lineNum = pos.line;
  let charNum = pos.character;
  try {
    const hover = await lsp.sendHover(uri, pos);
    if (hover === undefined || hover === null) {
      // console.log('[DEBUG] hover result: null or undefined');
    } else {
      // console.log('[DEBUG] hover result:', JSON.stringify(hover, null, 2));
    }
    if (hover && hover.contents) {
      // 适配 TS/JS: 提取 markdown 代码块中的方法签名
      const extractTSSignature = (text: string) => {
        // 匹配 ```typescript ... ``` 或 (method) ...
        const mdMatch = text.match(/```[a-zA-Z]*\n([\s\S]*?)```/);
        if (mdMatch && mdMatch[1]) {
          return mdMatch[1].split('\n').map(l => l.trim()).filter(Boolean)[0] || '';
        }
        // fallback: 匹配 (method) ...
        const methodMatch = text.match(/\(method\)[^\n]+/);
        if (methodMatch) return methodMatch[0];
        // fallback: 第一行
        return text.split('\n')[0].trim();
      };
      const extractDefSignature = (text: string, symbol: any, filePath?: string) => {
        // TS/JS: markdown 代码块优先
        if (/```/.test(text)) {
          const sig = extractTSSignature(text);
          let loc = '';
          if (filePath) {
            const relPath = path.relative(process.cwd(), filePath);
            loc = `[${relPath}:${lineNum + 1}:${charNum + 1}]`;
          } else {
            loc = `[line:${lineNum + 1}, char:${charNum + 1}]`;
          }
          return sig + '    ' + loc;
        }
        // Python: def ... 多行拼接
        text = text.replace(/^\(method\)\s*/, '');
        const defMatch = text.match(/def\s+\w+\s*\(/);
        if (!defMatch) {
          let loc = '';
          if (filePath) {
            const relPath = path.relative(process.cwd(), filePath);
            loc = `[${relPath}:${lineNum + 1}:${charNum + 1}]`;
          } else {
            loc = `[line:${lineNum + 1}, char:${charNum + 1}]`;
          }
          return text.split('\n')[0].trim() + '    ' + loc;
        }
        const defStart = text.indexOf(defMatch[0]);
        let lines = text.slice(defStart).split(/\r?\n/);
        let sig = '';
        let parenCount = 0;
        let started = false;
        for (let line of lines) {
          line = line.replace(/#.*/, '').replace(/:[^,\)]+/g, '');
          if (!started && /def\s+\w+\s*\(/.test(line)) started = true;
          if (started) {
            sig += line.trim() + ' ';
            for (const ch of line) {
              if (ch === '(') parenCount++;
              if (ch === ')') parenCount--;
            }
            if (parenCount <= 0 && line.includes(')')) break;
          }
        }
        sig = sig.replace(/\s+/g, ' ').replace(/\s*\)\s*/, ')').trim();
        if (!sig.endsWith(':')) sig += ':';
        let loc = '';
        if (filePath) {
          const relPath = path.relative(process.cwd(), filePath);
          loc = `[${relPath}:${lineNum + 1}:${charNum + 1}]`;
        } else {
          loc = `[line:${lineNum + 1}, char:${charNum + 1}]`;
        }
        sig += '    ' + loc;
        return sig;
      };
      if (typeof hover.contents === 'string') {
        return extractDefSignature(hover.contents, symbol, absPath);
      }
      if (Array.isArray(hover.contents)) {
        for (const c of hover.contents) {
          if (typeof c === 'string') {
            const res = extractDefSignature(c, symbol, absPath);
            if (res) return res;
          } else if (c.value) {
            const res = extractDefSignature(c.value, symbol, absPath);
            if (res) return res;
          }
        }
        const relPath = path.relative(process.cwd(), absPath);
        return hover.contents.map((c: any) => typeof c === 'string' ? c : (c.value || c.language + ': ' + c.value)).join('\n') + `    [${relPath}:${lineNum + 1}:${charNum + 1}]`;
      }
      if (hover.contents.value) {
        return extractDefSignature(hover.contents.value, symbol, absPath);
      }
      if (hover.contents.language && hover.contents.value) {
        return extractDefSignature(hover.contents.value, symbol, absPath);
      }
    }
    // 即使没有内容也要输出
    console.log('[DEBUG] hover result (no contents):', JSON.stringify(hover, null, 2));
  } catch (e) {
    console.error('[sendHover error]', e);
  }
  // hover 失败时也要返回 name + 行列号
  return symbol.name + `    [line:${lineNum}, char:${charNum}]`;
}

// 展示类定义（语义化）
export async function showClassDefinition(options: ShowOptions): Promise<ShowResult> {
  let { file, symbol, mode = "full" } = options;
  if (!symbol) throw new Error("必须指定 symbol 名称");
  let absPath = path.resolve(file);
  let filesToSearch: string[] = [];
  let firstClassSymbol: any = null;
  let firstFile: string = '';
  if (fs.existsSync(absPath) && fs.statSync(absPath).isDirectory()) {
    filesToSearch = glob.sync("**/*.{ts,js,tsx,jsx,py}", { cwd: absPath, absolute: true });
    filesToSearch = filesToSearch.filter(f => fs.readFileSync(f, "utf-8").includes(symbol));
    if (filesToSearch.length === 0) {
      throw new Error(`目录 ${file} 下未找到包含符号 ${symbol} 的文件`);
    }
  } else {
    filesToSearch = [absPath];
  }
  if (mode === "methods") {
    // 新实现：每个文件单独分组展示方法或函数
    let fileMethodMap: { [file: string]: string[] } = {};
    let total = 0;
    for (const f of filesToSearch) {
      const absPathF = f;
      const projectRoot = process.cwd();
      let lspLang = "typescript";
      const ext = path.extname(absPathF).toLowerCase();
      if ([".py"].includes(ext)) {
        lspLang = "python";
      } else if ([".ts", ".tsx", ".js", ".jsx"].includes(ext)) {
        lspLang = "typescript";
      } else {
        lspLang = "typescript";
      }
      const lsp = new LSPClient(`file://${projectRoot}`);
      await lsp.start(lspLang);
      try {
        const content = fs.readFileSync(absPathF, "utf-8");
        await lsp.sendDidOpen(`file://${absPathF}`, content, lspLang);
        const symbols = await lsp.sendDocumentSymbol(`file://${absPathF}`);
        if (!symbols) continue;
        const classSymbol = findClassSymbol(symbols, symbol);
        if (classSymbol) {
          // 只取一层，containerName 等于类名且 kind 为 Method/Constructor 的方法
          const methodList = symbols.filter((s: any) =>
            s.containerName === symbol && (s.kind === SymbolKind.Method || s.kind === SymbolKind.Constructor)
          );
          const signatures = await Promise.all(methodList.map((m: any) => getMethodSignature(lsp, absPathF, m)));
          const relFile = path.relative(process.cwd(), absPathF);
          fileMethodMap[relFile] = signatures;
          total += signatures.length;
          if (!firstClassSymbol) {
            firstClassSymbol = classSymbol;
            firstFile = absPathF;
          }
        } else {
          // 新增：查找同名顶层函数
          const funcSymbols = symbols.filter((s: any) => s.kind === SymbolKind.Function && s.name === symbol);
          if (funcSymbols.length > 0) {
            const signatures = await Promise.all(funcSymbols.map((m: any) => getMethodSignature(lsp, absPathF, m)));
            const relFile = path.relative(process.cwd(), absPathF);
            fileMethodMap[relFile] = signatures;
            total += signatures.length;
          }
        }
      } finally {
        await lsp.shutdown();
      }
    }
    // 拼接输出
    let content = Object.entries(fileMethodMap)
      .filter(([_, methods]) => methods.length > 0)
      .map(([file, methods]) => {
        return `文件: ${file}\n` + methods.join("\n");
      })
      .join("\n\n");
    return {
      file: filesToSearch.map(f => path.relative(process.cwd(), f)).join(', '),
      symbol,
      mode,
      startLine: 0,
      endLine: 0,
      content,
      totalLines: total,
    };
  }
  // 修正：遍历所有文件，找到第一个classSymbol就赋值并break
  for (const f of filesToSearch) {
    const absPathF = f;
    const projectRoot = process.cwd();
    let lspLang = "typescript";
    const ext = path.extname(absPathF).toLowerCase();
    if ([".py"].includes(ext)) {
      lspLang = "python";
    } else if ([".ts", ".tsx", ".js", ".jsx"].includes(ext)) {
      lspLang = "typescript";
    } else {
      lspLang = "typescript";
    }
    const lsp = new LSPClient(`file://${projectRoot}`);
    await lsp.start(lspLang);
    try {
      const content = fs.readFileSync(absPathF, "utf-8");
      await lsp.sendDidOpen(`file://${absPathF}`, content, lspLang);
      const symbols = await lsp.sendDocumentSymbol(`file://${absPathF}`);
      if (!symbols) continue;
      const classSymbol = findClassSymbol(symbols, symbol);
      if (classSymbol) {
        firstClassSymbol = classSymbol;
        firstFile = absPathF;
        await lsp.sendDidClose(`file://${absPathF}`);
        break;
      }
      await lsp.sendDidClose(`file://${absPathF}`);
    } finally {
      await lsp.shutdown();
    }
  }
  if (!firstClassSymbol) throw new Error(`未找到符号 ${symbol} 的定义`);
  const content = fs.readFileSync(firstFile, "utf-8");
  const lines = content.split("\n");
  let startLine = 1;
  let endLine = lines.length;
  if (firstClassSymbol && firstClassSymbol.location) {
    startLine = firstClassSymbol.location.range.start.line + 1;
    endLine = firstClassSymbol.location.range.end.line + 1;
  }
  let resultContent = "";
  if (mode === "full") {
    resultContent = lines.slice(startLine - 1, endLine).join("\n");
  } else if (mode === "init") {
    const initList = collectInitFlat(firstClassSymbol.children || [], symbol);
    resultContent = initList.join("\n");
  }
  return {
    file: path.relative(process.cwd(), firstFile),
    symbol,
    mode,
    startLine,
    endLine,
    content: resultContent,
    totalLines: lines.length,
  };
} 